/**
 * Custom Kit Colour Preview
 * ------------------------------------------------------------
 * PaintlyKit custom addition (not stock Horizon).
 *
 * Watches the existing photo-upload widget (snippets/image-uploader.liquid
 * — identified by its `.custom-image-uploader` wrapper and `#fileInput`
 * input) for a chosen file. As soon as a photo is chosen — no click
 * required — it runs a client-side colour quantization pass (a small
 * k-means implementation) entirely in the browser, no upload or network
 * round trip required, and shows the photo flattened/posterized to look
 * roughly like it would once painted with the kit's fixed colour
 * palette, relocated to sit directly ABOVE the upload widget itself
 * (see _relocateNextToUploader below). This is a purely visual preview:
 * it does not disclose the exact colours or the colour count anywhere
 * in the UI.
 *
 * The photo is rendered onto an actual canvas-shaped rectangle — sized
 * to the real aspect ratio of the selected Size variant (e.g. 40 x 40
 * cm vs 40 x 30 cm vs 70 x 50 cm), printed edge-to-edge like the real
 * kit, white background, lifted off the page with a soft shadow — sat
 * inside a visibly bigger, softly shaded "workspace" rectangle behind
 * it, so there's always empty space around the canvas making its edges
 * obvious, rather than the photo just filling a flat box with no
 * visible boundary. A small caption underneath echoes the exact size
 * selected (e.g. "50 x 70 cm"), read straight from the Size <select>'s
 * own option text — see _updateAspectRatio, which drives both the
 * --kit-aspect custom property (set on the stage/canvas element, not
 * the image — the image just fills whatever shape the stage ends up
 * being) and the caption text. This updates live if the shopper changes
 * the size selector — no re-processing needed, it's a pure CSS re-crop.
 *
 * For non-square sizes, the crop orientation (portrait vs landscape) is
 * auto-matched to the uploaded photo's own orientation rather than
 * strictly following the size label's literal width-by-height order —
 * e.g. a portrait (taller-than-wide) photo against a "40 x 30 cm"
 * (landscape-labelled) size still previews as a portrait-shaped crop,
 * so the preview always looks like a sensible crop of the customer's
 * own photo rather than an oddly-cropped one.
 *
 * Size-selector tracking is done via a document-level 'change' listener
 * that re-identifies the Size <select> from the event target each time,
 * rather than caching a single element reference — Shopify Horizon can
 * re-render (morph) parts of the variant-picker DOM on selection, which
 * would silently detach a listener bound to a since-replaced node.
 *
 * A "Crop to fill" / "Crop to fit" toggle lets the shopper switch
 * between object-fit: cover (default — crops the photo to fill the
 * canvas shape exactly, no white space) and object-fit: contain (fits
 * the whole photo inside the canvas shape, adding white space on the
 * sides that don't match, so nothing/no one near the edges of the photo
 * gets cropped out). Purely a CSS swap — no reprocessing needed, same as
 * a size change.
 *
 * Whichever crop option is active is written into a hidden
 * `properties[Photo Crop Style]` form field (see _setFitMode) so it's
 * captured as a line item property on the order — visible in Shopify
 * admin (and on the cart/checkout) — letting the merchant see exactly
 * which crop behaviour the shopper expects without guessing.
 *
 * Whenever the uploaded photo's own aspect ratio is a poor match for the
 * selected size while cropping is active — the classic support issue:
 * a wide group photo against a square canvas — a small hint message
 * (see _updateFitHint) nudges the shopper toward "Crop to fit" or a
 * better-fitting size, so people near the edges don't get silently
 * cropped out.
 *
 * This element relocates itself to sit directly above the upload widget
 * at runtime, regardless of where it was originally placed in the
 * template, so it always reads as part of that upload step.
 *
 * Scoped for use on the Custom Paint by Numbers Kit product template
 * only (templates/product.custom-kit.json).
 * ------------------------------------------------------------
 */

const RELOCATE_MAX_ATTEMPTS = 50; // ~10s at 200ms
const RELOCATE_INTERVAL_MS = 200;

// How far apart the photo's aspect ratio and the crop's aspect ratio can be
// (as a ratio of the larger to the smaller) before we consider it a poor
// enough match to warn the shopper — e.g. a typical group photo (~1.5–1.8)
// against a square canvas (1) sits well above this; a photo that's already
// a close match sits well below it.
const ASPECT_MISMATCH_WARN_THRESHOLD = 1.3;

/**
 * Pulls a "W x H" (or "W×H") pair out of a size label like "40 x 30 cm"
 * and returns the width/height ratio. Deliberately generic — doesn't
 * hardcode the store's specific size list, so new sizes just work.
 */
function parseAspectRatio(label) {
  if (!label) return null;
  const match = String(label).match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  const w = parseFloat(match[1]);
  const h = parseFloat(match[2]);
  if (!w || !h) return null;
  return w / h;
}

/**
 * Heuristic to identify "the Size selector" among all <select> elements
 * on the page without depending on a specific name/id: a select whose
 * options mostly look like dimension pairs ("40 x 40 cm") is almost
 * certainly it, and this avoids false-matching the Frame selector
 * ("No Frame" / "DIY Frame" / "Framed").
 */
function countDimensionOptions(select) {
  let matches = 0;
  for (const opt of select.options) {
    if (parseAspectRatio(opt.text) || parseAspectRatio(opt.value)) matches += 1;
  }
  return matches;
}

function looksLikeSizeSelect(select) {
  return !!select && select.tagName === 'SELECT' && countDimensionOptions(select) >= 2;
}

/**
 * Live-searches the whole page for the Size <select> right now, rather
 * than relying on a cached reference that might have gone stale if the
 * theme re-rendered that part of the DOM.
 */
function findSizeSelect() {
  const selects = document.querySelectorAll('select');
  let best = null;
  let bestMatches = 0;
  selects.forEach((select) => {
    const matches = countDimensionOptions(select);
    if (matches >= 2 && matches > bestMatches) {
      best = select;
      bestMatches = matches;
    }
  });
  return best;
}

class CustomKitColourPreview extends HTMLElement {
  constructor() {
    super();
    this._onFileChange = this._onFileChange.bind(this);
    this._onDocumentChange = this._onDocumentChange.bind(this);
    this._onFitToggleClick = this._onFitToggleClick.bind(this);
    this._cache = null; // { key, dataUrl }
    this._aspectRatio = 1;
    this._photoAspect = null; // naturalWidth / naturalHeight of the uploaded photo
    this._fitMode = 'cover'; // 'cover' (crop to fill) or 'contain' (crop to fit)
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-preview-panel]');
    this.statusEl = this.querySelector('[data-preview-status]');
    this.stageEl = this.querySelector('[data-preview-stage]');
    this.imageEl = this.querySelector('[data-preview-image]');
    this.fitToggle = this.querySelector('[data-preview-fit-toggle]');
    this.hintEl = this.querySelector('[data-preview-hint]');
    this.dimensionsEl = this.querySelector('[data-preview-dimensions]');
    this.cropStyleField = this.querySelector('[data-crop-style-field]');

    this.colourCount = parseInt(this.dataset.colourCount, 10) || 24;

    // Delegated at the document level (see file header) so it keeps
    // working even if the theme re-renders the Size <select> itself.
    document.addEventListener('change', this._onDocumentChange);
    this.fitToggle?.addEventListener('click', this._onFitToggleClick);

    this._relocateNextToUploader();
    this._watchForFileInput();
    this._updateAspectRatio();
  }

  disconnectedCallback() {
    this._fileInput?.removeEventListener('change', this._onFileChange);
    document.removeEventListener('change', this._onDocumentChange);
    this.fitToggle?.removeEventListener('click', this._onFitToggleClick);
    if (this._relocateTimer) clearInterval(this._relocateTimer);
    if (this._inputTimer) clearInterval(this._inputTimer);
  }

  /**
   * Moves this element to sit right ABOVE the photo upload widget (i.e.
   * immediately before it in the DOM), so the preview reads as "here's
   * what you're about to get" ahead of the upload control itself —
   * regardless of where the block was originally placed in the
   * template.
   */
  _relocateNextToUploader() {
    let attempts = 0;
    const tryMove = () => {
      attempts += 1;
      const uploader = document.querySelector('.custom-image-uploader');
      if (uploader && uploader.parentElement) {
        // Moving a custom element (even to the same spot) re-fires
        // disconnectedCallback/connectedCallback, which would call back
        // into this exact function — only move if we're not already
        // there, or repeated moves recurse until the stack overflows.
        if (uploader.previousElementSibling !== this) {
          uploader.insertAdjacentElement('beforebegin', this);
        }
        clearInterval(this._relocateTimer);
        return;
      }
      if (attempts >= RELOCATE_MAX_ATTEMPTS) {
        clearInterval(this._relocateTimer);
      }
    };
    tryMove();
    if (!this._relocateTimer) {
      this._relocateTimer = setInterval(tryMove, RELOCATE_INTERVAL_MS);
    }
  }

  _watchForFileInput() {
    let attempts = 0;
    const tryBind = () => {
      attempts += 1;
      const input = document.getElementById('fileInput');
      if (input && input !== this._fileInput) {
        this._fileInput = input;
        input.addEventListener('change', this._onFileChange);
        clearInterval(this._inputTimer);
        return;
      }
      if (attempts >= RELOCATE_MAX_ATTEMPTS) {
        clearInterval(this._inputTimer);
      }
    };
    tryBind();
    if (!this._inputTimer) {
      this._inputTimer = setInterval(tryBind, RELOCATE_INTERVAL_MS);
    }
  }

  /**
   * Fires on every 'change' event anywhere on the page (cheap — change
   * events are infrequent); only reacts if the changed element itself
   * looks like the Size selector.
   */
  _onDocumentChange(event) {
    if (looksLikeSizeSelect(event.target)) {
      this._updateAspectRatio();
    }
  }

  /**
   * Works out the crop's aspect ratio from the currently selected Size
   * option, auto-flipping portrait/landscape to match the uploaded
   * photo's own orientation when the size itself isn't square. Also
   * updates the --kit-aspect custom property on the CANVAS ("stage")
   * element — not the image — since the stage is what's actually sized
   * to the real canvas shape now; the image just fills whatever shape
   * the stage ends up being. And updates the small "50 x 70 cm"-style
   * caption underneath from the same option's own label text.
   */
  _updateAspectRatio() {
    const select = findSizeSelect();
    const opt = select ? select.options[select.selectedIndex] : null;
    const label = opt ? (opt.text || opt.value) : null;
    const sizeRatio = parseAspectRatio(label);

    let ratio = sizeRatio || 1;

    if (sizeRatio && Math.abs(sizeRatio - 1) > 0.001 && this._photoAspect) {
      const photoIsLandscape = this._photoAspect > 1;
      const sizeIsLandscape = sizeRatio > 1;
      if (photoIsLandscape !== sizeIsLandscape) {
        // Flip the size's shape so it matches the photo's own orientation.
        ratio = 1 / sizeRatio;
      }
    }

    this._aspectRatio = ratio;
    if (this.stageEl) {
      this.stageEl.style.setProperty('--kit-aspect', this._aspectRatio);
    }
    if (this.dimensionsEl) {
      const trimmed = label ? label.trim() : '';
      this.dimensionsEl.textContent = trimmed;
      this.dimensionsEl.hidden = !trimmed;
    }
    this._updateFitHint();
  }

  /**
   * Shows a small warning message when the uploaded photo's own shape is a
   * poor match for the crop it's being squeezed into while cropping is
   * active — the classic case being a wide group photo against a square
   * canvas, where "Crop to fill" would otherwise silently cut people off
   * the sides without the shopper noticing until the kit arrives. Hidden
   * whenever there's no photo yet, the mismatch is minor, or the shopper
   * has already switched to "Crop to fit" (which shows the whole
   * photo, so there's nothing to warn about).
   */
  _updateFitHint() {
    if (!this.hintEl) return;
    if (!this._photoAspect || this._fitMode !== 'cover') {
      this.hintEl.hidden = true;
      return;
    }
    const larger = Math.max(this._photoAspect, this._aspectRatio);
    const smaller = Math.min(this._photoAspect, this._aspectRatio);
    this.hintEl.hidden = larger / smaller < ASPECT_MISMATCH_WARN_THRESHOLD;
  }

  /**
   * Handles clicks on the "Crop to fill" / "Crop to fit" buttons.
   * Purely a CSS object-fit swap on the already-rendered image — no
   * reprocessing needed.
   */
  _onFitToggleClick(event) {
    const btn = event.target.closest('[data-fit-mode]');
    if (!btn || !this.fitToggle?.contains(btn)) return;
    this._setFitMode(btn.dataset.fitMode);
  }

  _setFitMode(mode) {
    this._fitMode = mode === 'contain' ? 'contain' : 'cover';
    if (this.imageEl) {
      this.imageEl.style.objectFit = this._fitMode;
    }
    this.fitToggle?.querySelectorAll('[data-fit-mode]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.fitMode === this._fitMode));
    });
    // Keep the hidden "Photo Crop Style" line item property in sync so
    // whichever option the shopper lands on is recorded on the order.
    if (this.cropStyleField) {
      this.cropStyleField.value = this._fitMode === 'contain' ? 'Crop to fit' : 'Crop to fill';
    }
    this._updateFitHint();
  }

  _onFileChange(event) {
    const file = event.target.files && event.target.files[0];
    if (!file || !file.type || file.type.indexOf('image/') !== 0) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      this._sourceDataUrl = e.target.result;
      this._cache = null;
      this._photoAspect = null;
      // Reset to the default crop style for each newly-uploaded photo.
      this._setFitMode('cover');
      this._startPreview();
    };
    reader.readAsDataURL(file);
  }

  /**
   * Kicks off processing automatically as soon as a photo is uploaded —
   * no click required.
   */
  _startPreview() {
    if (!this._sourceDataUrl || !this.panel) return;

    this.panel.hidden = false;

    if (this._cache && this._cache.key === this._sourceDataUrl) {
      this._renderResult(this._cache);
      return;
    }

    this._showProcessing();

    // Defer the heavy work one frame so the "processing" state actually paints first.
    requestAnimationFrame(() => {
      setTimeout(() => this._process(), 0);
    });
  }

  _showProcessing() {
    if (this.statusEl) {
      this.statusEl.hidden = false;
      this.statusEl.textContent = 'Analysing your photo…';
    }
    if (this.imageEl) this.imageEl.hidden = true;
    if (this.fitToggle) this.fitToggle.hidden = true;
    if (this.hintEl) this.hintEl.hidden = true;
  }

  _process() {
    const img = new Image();
    img.onload = () => {
      try {
        this._photoAspect = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
        this._updateAspectRatio();

        const result = quantizeImage(img, this.colourCount);
        this._cache = { key: this._sourceDataUrl, ...result };
        this._renderResult(this._cache);
      } catch (err) {
        console.error('Custom kit colour preview failed:', err);
        if (this.statusEl) {
          this.statusEl.hidden = false;
          this.statusEl.textContent = "Sorry, we couldn't generate a preview for this photo.";
        }
      }
    };
    img.onerror = () => {
      if (this.statusEl) {
        this.statusEl.hidden = false;
        this.statusEl.textContent = "Sorry, we couldn't read that photo.";
      }
    };
    img.src = this._sourceDataUrl;
  }

  _renderResult({ dataUrl }) {
    if (this.statusEl) this.statusEl.hidden = true;

    if (this.imageEl) {
      this.imageEl.style.objectFit = this._fitMode;
      this.imageEl.src = dataUrl;
      this.imageEl.hidden = false;
    }
    if (this.fitToggle) this.fitToggle.hidden = false;
  }
}

/**
 * Draws `img` onto a canvas capped at `maxSize` on the longest side and
 * returns the canvas + its pixel data.
 */
function drawScaled(img, maxSize) {
  const scale = Math.min(1, maxSize / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
  const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, width, height);
  return { canvas, ctx, width, height };
}

/**
 * Simple k-means colour quantization. Clusters on a small downscaled
 * sample for speed, then maps the full display-resolution image to the
 * nearest cluster colour to produce a flattened, paint-by-numbers-style
 * preview. Returns { dataUrl } — a purely visual result; the palette
 * itself is intentionally not surfaced anywhere in the UI.
 */
function quantizeImage(img, k) {
  const CLUSTER_SIZE = 120; // sampling resolution for finding the palette
  const DISPLAY_SIZE = 560; // resolution of the final preview image
  const ITERATIONS = 8;

  const { ctx: clusterCtx, width: cw, height: ch } = drawScaled(img, CLUSTER_SIZE);
  const clusterData = clusterCtx.getImageData(0, 0, cw, ch).data;

  const samples = [];
  for (let i = 0; i < clusterData.length; i += 4) {
    if (clusterData[i + 3] < 16) continue; // skip near-transparent pixels
    samples.push([clusterData[i], clusterData[i + 1], clusterData[i + 2]]);
  }
  if (samples.length === 0) samples.push([200, 200, 200]);

  const clampedK = Math.max(2, Math.min(k, samples.length));

  // Seed centroids evenly across the sample set for a more stable start
  // than pure random picks.
  const centroids = [];
  for (let i = 0; i < clampedK; i++) {
    const idx = Math.floor((i / clampedK) * samples.length);
    centroids.push(samples[idx].slice());
  }

  const assignments = new Array(samples.length).fill(0);

  for (let iter = 0; iter < ITERATIONS; iter++) {
    // Assign
    for (let s = 0; s < samples.length; s++) {
      const [r, g, b] = samples[s];
      let best = 0;
      let bestDist = Infinity;
      for (let c = 0; c < centroids.length; c++) {
        const cc = centroids[c];
        const dr = r - cc[0];
        const dg = g - cc[1];
        const db = b - cc[2];
        const dist = dr * dr + dg * dg + db * db;
        if (dist < bestDist) {
          bestDist = dist;
          best = c;
        }
      }
      assignments[s] = best;
    }

    // Recompute
    const sums = centroids.map(() => [0, 0, 0, 0]);
    for (let s = 0; s < samples.length; s++) {
      const c = assignments[s];
      sums[c][0] += samples[s][0];
      sums[c][1] += samples[s][1];
      sums[c][2] += samples[s][2];
      sums[c][3] += 1;
    }
    for (let c = 0; c < centroids.length; c++) {
      if (sums[c][3] > 0) {
        centroids[c][0] = sums[c][0] / sums[c][3];
        centroids[c][1] = sums[c][1] / sums[c][3];
        centroids[c][2] = sums[c][2] / sums[c][3];
      } else {
        // Re-seed empty clusters from a random sample so we always end up
        // with the requested number of colours where possible.
        const idx = Math.floor(Math.random() * samples.length);
        centroids[c] = samples[idx].slice();
      }
    }
  }

  // Render the full preview at display resolution using the final palette.
  const { canvas: displayCanvas, ctx: displayCtx, width: dw, height: dh } = drawScaled(img, DISPLAY_SIZE);
  const imageData = displayCtx.getImageData(0, 0, dw, dh);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 16) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    let best = 0;
    let bestDist = Infinity;
    for (let c = 0; c < centroids.length; c++) {
      const cc = centroids[c];
      const dr = r - cc[0];
      const dg = g - cc[1];
      const db = b - cc[2];
      const dist = dr * dr + dg * dg + db * db;
      if (dist < bestDist) {
        bestDist = dist;
        best = c;
      }
    }
    data[i] = centroids[best][0];
    data[i + 1] = centroids[best][1];
    data[i + 2] = centroids[best][2];
  }

  displayCtx.putImageData(imageData, 0, 0);

  return {
    dataUrl: displayCanvas.toDataURL('image/jpeg', 0.9),
  };
}

if (!customElements.get('custom-kit-colour-preview')) {
  customElements.define('custom-kit-colour-preview', CustomKitColourPreview);
}
